import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Send, MessageCircle, Search, Crown } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { Avatar } from "@/components/PostCard";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Messages() {
  const { user } = useAuth();
  const { otherId } = useParams();
  const navigate = useNavigate();
  const [convos, setConvos] = useState([]);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const loadConvos = useCallback(() => { api.get("/conversations").then(({ data }) => setConvos(data)).catch(() => {}); }, []);
  useEffect(() => { loadConvos(); }, [loadConvos]);

  const loadThread = useCallback(() => {
    if (!otherId) { setThread(null); return; }
    api.get(`/messages/${otherId}`).then(({ data }) => setThread(data)).catch(() => {});
  }, [otherId]);
  useEffect(() => { loadThread(); }, [loadThread]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      await api.post("/messages", { recipient_id: otherId, text });
      setText(""); loadThread(); loadConvos();
    } catch (e) {
      if (e.response?.status === 403) { toast.error("Artist Pass required to send messages"); navigate("/subscribe"); }
      else toast.error("Could not send");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="nu-card overflow-hidden grid grid-cols-1 md:grid-cols-3 h-[calc(100vh-10rem)]">
        {/* Conversations */}
        <div className={`border-r border-white/10 flex-col ${otherId ? "hidden md:flex" : "flex"}`}>
          <div className="p-4 border-b border-white/10">
            <h2 className="font-display font-extrabold text-xl">Messages</h2>
          </div>
          <div className="flex-1 overflow-y-auto hide-scrollbar" data-testid="conversations-list">
            {convos.length === 0 ? (
              <div className="p-6 text-center text-zinc-500 text-sm">
                <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                No conversations yet. Message an artist from their profile.
              </div>
            ) : convos.map((c) => (
              <button key={c.artist.id} onClick={() => navigate(`/messages/${c.artist.id}`)} data-testid="conversation-item"
                className={`w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-colors text-left ${otherId === c.artist.id ? "bg-white/5" : ""}`}>
                <Avatar artist={c.artist} size={44} />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">{c.artist.artist_name}</p>
                  <p className="text-xs text-zinc-500 truncate">{c.last_message}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className={`md:col-span-2 flex-col ${otherId ? "flex" : "hidden md:flex"}`}>
          {!thread ? (
            <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">Select a conversation</div>
          ) : (
            <>
              <div className="p-4 border-b border-white/10 flex items-center gap-3">
                <button onClick={() => navigate("/messages")} className="md:hidden text-zinc-400">←</button>
                <Avatar artist={thread.other} size={40} />
                <p className="font-bold">{thread.other?.artist_name}</p>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 hide-scrollbar" data-testid="message-thread">
                {thread.messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-4 py-2 text-sm ${mine ? "bg-emerald-600 text-white rounded-2xl rounded-tr-none" : "bg-zinc-800 text-zinc-100 rounded-2xl rounded-tl-none"}`}>
                        {m.text}
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="p-4 border-t border-white/10 flex items-center gap-2">
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Type a message…" data-testid="message-input"
                  className="flex-1 bg-black/40 border border-white/10 rounded-full px-4 py-2.5 text-sm outline-none focus:border-emerald-500/50" />
                <button onClick={send} data-testid="message-send" className="w-10 h-10 rounded-full bg-emerald-500 text-black flex items-center justify-center shrink-0"><Send className="w-4 h-4" /></button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
